import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from 'src/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary font-semibold text-primary-foreground hover:bg-primary/90',
        destructive:
          'bg-destructive font-semibold text-destructive-foreground hover:bg-destructive/90',
        // Secondary actions are a blue outline (restyle, 2026-10-28).
        outline:
          'border-[1.5px] border-primary bg-transparent font-semibold text-[var(--color-accent-blue-dark)] hover:bg-[var(--color-accent-blue-glow)]',
        secondary:
          'border-[1.5px] border-primary bg-transparent font-semibold text-[var(--color-accent-blue-dark)] hover:bg-[var(--color-accent-blue-glow)]',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-[var(--color-link)] underline-offset-4 hover:text-[var(--color-link-hover)] hover:underline',
        // The ONE call to action on a screen (New job, Review now). At most one per screen.
        cta: 'bg-action font-semibold text-action-foreground hover:bg-action/90',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-full px-3',
        lg: 'h-11 rounded-full px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
